import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { store } from '../store/store'
import { logout } from '../store/slices/authSlice'

// Use environment variable for API URL - defaults to relative path for same-origin
const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send HttpOnly cookies with every request
})

const publicEndpoints = [
  '/health',
  '/auth/login',
  '/auth/register',
  '/auth/google',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/refresh',
  '/auth/verify-email',
  '/auth/contact',
  '/api/shared/',
]

// Helper to get CSRF token from cookie
function getCSRFToken(): string | null {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === 'csrftoken') {
      return decodeURIComponent(value)
    }
  }
  return null
}

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add trailing slash to URLs (Django requires this for POST requests)
    if (config.url && !config.url.endsWith('/') && !config.url.includes('?')) {
      config.url = `${config.url}/`
    }

    // Add CSRF token for state-changing requests
    if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method)) {
      const csrfToken = getCSRFToken()
      if (csrfToken && config.headers) {
        config.headers['X-CSRFToken'] = csrfToken
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (error: Error) => void
}> = []

const processQueue = (error: Error | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const isAuthEndpoint = publicEndpoints.some(endpoint =>
        originalRequest.url?.startsWith(endpoint) || originalRequest.url?.startsWith(endpoint + '/')
      )

      if (isAuthEndpoint) {
        const errorMessage = (error.response?.data as { error?: string })?.error || error.message || 'Authentication failed'
        return Promise.reject(new Error(errorMessage))
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            // Cookies are automatically updated by the refresh response
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Refresh token is sent automatically via HttpOnly cookie
        await axios.post(`${API_BASE_URL}/auth/refresh/`, {}, {
          withCredentials: true,
        })

        processQueue(null)
        isRefreshing = false

        // Retry original request — cookies are already updated
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(new Error('Token refresh failed'))
        store.dispatch(logout())
        isRefreshing = false
        return Promise.reject(refreshError)
      }
    }

    const errorMessage = (error.response?.data as { error?: string })?.error || error.message || 'Network error'
    return Promise.reject(new Error(errorMessage))
  }
)

export const authService = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (data: {
    email: string
    password: string
    first_name?: string
    last_name?: string
  }) => api.post('/auth/register', data),

  googleLogin: (credential: string) =>
    api.post('/auth/google', { credential }),

  logout: () =>
    api.post('/auth/logout', {}),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),

  refreshToken: () =>
    api.post('/auth/refresh', {}),

  getCurrentUser: () => api.get('/auth/me'),

  getProfile: () => api.get('/auth/profile'),

  updateProfile: (data: { first_name?: string; last_name?: string }) =>
    api.patch('/auth/profile', data),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  verifyEmail: (token: string) =>
    api.post('/auth/verify-email', { token }),

  resendVerification: () =>
    api.post('/auth/resend-verification', {}),

  submitContact: (data: { name: string; email: string; subject: string; message: string }) =>
    api.post('/auth/contact', data),
}

export const apiService = {
  healthCheck: () => api.get('/health'),

  ask: (data: {
    mode?: 'pinecone' | 'csv'
    namespace?: string
    file_id?: string
    thread_id?: string
    messages: Array<{ question: string }>
  }) => api.post('/ask', data, { timeout: 120000 }),

  getNamespaces: () => api.get('/namespaces'),
  deleteNamespace: (namespace: string) => api.delete(`/namespaces/${encodeURIComponent(namespace)}`),

  uploadDocument: (file: File, namespace: string, createNew: boolean = false, category?: string, tags?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('namespace', namespace)
    formData.append('create_new', createNew.toString())
    if (category) formData.append('category', category)
    if (tags) formData.append('tags', tags)

    return api.post('/upload_documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  query: (data: { query: string; thread_id?: string; namespace?: string }) =>
    api.post('/ask', {
      namespace: data.namespace || 'default_namespace',
      thread_id: data.thread_id,
      messages: [{ question: data.query }],
    }),

  getConversations: () => api.get('/conversations'),

  createConversation: (data: { title: string; namespace?: string }) =>
    api.post('/conversations', data),

  getConversation: (id: string) => api.get(`/conversations/${id}`),

  updateConversation: (id: string, data: { title: string }) =>
    api.put(`/conversations/${id}`, data),

  deleteConversation: (id: string) => api.delete(`/conversations/${id}`),

  addMessage: (conversationId: string, data: { role: string; content: string }) =>
    api.post(`/conversations/${conversationId}/messages`, data),

  uploadCsvFile: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    return api.post('/upload_csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  getCsvFiles: () => api.get('/csv_files'),

  loadCsvFile: (fileId: string) => api.get(`/csv_files/${fileId}`),

  deleteCsvFile: (fileId: string) => api.delete(`/csv_files/${fileId}`),

  getDashboardStats: () => api.get('/api/dashboard/stats'),

  getRiskRadar: () => api.get('/api/dashboard/risk-radar'),

  getRiskTrend: (months: number = 6) =>
    api.get(`/api/dashboard/risk-trend?months=${months}`),

  getRiskDistribution: () => api.get('/api/dashboard/risk-distribution'),

  getActivities: (limit: number = 10) =>
    api.get(`/api/dashboard/activities?limit=${limit}`),

  getAudits: (limit: number = 50) => api.get(`/api/audits?limit=${limit}`),

  createAudit: (data: { title: string; namespace?: string; frameworks?: string[] }) =>
    api.post('/api/audits', data),

  getAudit: (auditId: string) => api.get(`/api/audits/${auditId}`),

  updateAudit: (auditId: string, data: Record<string, unknown>) =>
    api.put(`/api/audits/${auditId}`, data),

  deleteAudit: async (auditId: string) => {
    try {
      return await api.delete(`/api/audits/${auditId}`)
    } catch (error) {
      if (error instanceof Error && error.message.includes('405')) {
        return await api.post(`/api/audits/${auditId}/delete`)
      }
      throw error
    }
  },

  analyzeAudit: (auditId: string) => api.post(`/api/audits/${auditId}/analyze`),

  exportAuditPdf: async (auditId: string) => {
    const response = await api.get(`/api/audits/${auditId}/export`, {
      responseType: 'blob',
    })
    return response.data
  },

  uploadDocumentsBatch: (files: File[], namespace: string, createNew: boolean = false, category?: string, tags?: string) => {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    formData.append('namespace', namespace)
    formData.append('create_new', createNew.toString())
    if (category) formData.append('category', category)
    if (tags) formData.append('tags', tags)

    return api.post('/upload_documents/batch', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  getBatchJobStatus: (jobId: string) => api.get(`/upload_documents/batch/${jobId}`),

  listBatchJobs: () => api.get('/upload_documents/batch'),

  uploadRegulations: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    return api.post('/api/regulations/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  listRegulations: () => api.get('/api/regulations/list'),

  searchRegulations: (query: string, regulationType?: string, limit?: number) =>
    api.post('/api/regulations/search', { query, regulation_type: regulationType, limit }),

  extractClausesFromText: (text: string, title?: string) =>
    api.post('/api/clauses/extract', { text, title }),

  extractClausesFromNamespace: (namespace: string) =>
    api.post(`/api/clauses/extract/namespace/${namespace}`),

  extractClausesForAudit: (auditId: string) =>
    api.post(`/api/audits/${auditId}/extract-clauses`),

  extractClausesFromFile: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    return api.post('/api/clauses/extract/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  validateClauses: (clauses: Array<{ id?: string; clause_number?: string; topic?: string; text: string }>) =>
    api.post('/api/compliance/validate', { clauses }),

  validateSingleClause: (clause: { id?: string; clause_number?: string; topic?: string; text: string }) =>
    api.post('/api/compliance/validate/clause', { clause }),

  validateAuditClauses: (auditId: string) =>
    api.post(`/api/audits/${auditId}/validate`),

  generateRemediation: (violation: Record<string, unknown>, regulation?: Record<string, unknown>) =>
    api.post('/api/remediation/generate', { violation, regulation }),

  generateBatchRemediations: (violations: Array<Record<string, unknown>>, regulationsMap?: Record<string, unknown>) =>
    api.post('/api/remediation/generate/batch', { violations, regulations_map: regulationsMap }),

  generateAuditRemediations: (auditId: string, violationIds?: string[]) =>
    api.post(`/api/audits/${auditId}/remediate`, { violation_ids: violationIds }),

  verifyFinding: (finding: Record<string, unknown>) =>
    api.post('/api/critic/verify', { finding }),

  verifyFindingsBatch: (findings: Array<Record<string, unknown>>) =>
    api.post('/api/critic/verify/batch', { findings }),

  verifyAuditFindings: (auditId: string) =>
    api.post(`/api/audits/${auditId}/verify`),

  getHumanReviewItems: (auditId: string) =>
    api.get(`/api/audits/${auditId}/review-items`),

  toggleShare: (auditId: string) =>
    api.post(`/api/audits/${auditId}/share`),

  getSharedAudit: (token: string) =>
    api.get(`/api/shared/${token}`),
}

export const feedbackService = {
  list: () =>
    api.get('/api/feedback'),

  submit: (data: { type: string; title: string; description: string; rating?: number }) =>
    api.post('/api/feedback', data),

  delete: (id: string) =>
    api.delete(`/api/feedback/${id}`),
}

export const billingService = {
  getSubscription: () =>
    api.get('/api/billing/subscription'),

  getCredits: () =>
    api.get('/api/billing/credits'),

  createCheckoutSession: (
    plan: 'free' | 'pay_once' | 'starter' | 'growth',
  ) =>
    api.post('/api/billing/create-checkout-session', { plan, billing_cycle: 'monthly' }),

  createPortalSession: () =>
    api.post('/api/billing/create-portal-session'),

  getUsage: () =>
    api.get('/api/billing/usage'),
}

export default api
